import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router';
import { api, unwrap } from '../api/client';
import { useAgents, useServices } from '../api/hooks';
import { ErrorNote, Loading, PageHead, RoleBadge, formatDateTime } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
}

const ROLES = [
  { value: 'agent', label: 'Agent' },
  { value: 'chef_service', label: 'Chef de service' },
  { value: 'drh', label: 'DRH' },
  { value: 'elu', label: 'Élu' },
  { value: 'admin', label: 'Administrateur' },
] as const;

/** Comptes de la collectivité et rôles attribués. Réservé à l'administrateur (lecture pour la DRH). */
export function RolesPage() {
  const me = useOutletContext<MeContext>();
  const isAdmin = me.roles.some((r) => r.role === 'admin');
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: async () => unwrap(await api.GET('/api/v1/users')) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => unwrap(await api.GET('/api/v1/roles')) });
  const services = useServices();
  const agents = useAgents();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['roles'] });
    void qc.invalidateQueries({ queryKey: ['agents'] });
  };

  const userLabel = (id: string) => {
    const u = users.data?.find((x) => x.id === id);
    return u ? `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.username : id.slice(0, 8);
  };
  const serviceLabel = (id: string | null) => (id ? (services.data?.find((s) => s.id === id)?.code ?? '…') : '');

  // Attribution de rôle -----------------------------------------------------
  const roleDialog = useRef<HTMLDialogElement>(null);
  const [roleForm, setRoleForm] = useState({ userId: '', role: 'agent', serviceId: '', validTo: '' });
  const assign = useMutation({
    mutationFn: async () => {
      const u = users.data?.find((x) => x.id === roleForm.userId);
      return unwrap(
        await api.POST('/api/v1/roles', {
          body: {
            userId: roleForm.userId,
            userLabel: u?.username ?? roleForm.userId,
            role: roleForm.role as 'agent',
            serviceId: roleForm.role === 'chef_service' ? roleForm.serviceId || null : null,
            validTo: roleForm.validTo ? new Date(roleForm.validTo).toISOString() : null,
          },
        }),
      );
    },
    onSuccess: () => {
      invalidate();
      roleDialog.current?.close();
    },
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => unwrap(await api.DELETE('/api/v1/roles/{id}', { params: { path: { id } } })),
    onSuccess: invalidate,
  });

  // Création de compte ------------------------------------------------------
  const userDialog = useRef<HTMLDialogElement>(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', firstName: '', lastName: '', temporaryPassword: '', requireTotp: false, agentId: '' });
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);
  const createUser = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/users', {
          body: { ...userForm, agentId: userForm.agentId || null },
        }),
      ),
    onSuccess: (u) => {
      setCreated({ username: u.username, password: userForm.temporaryPassword });
      invalidate();
      userDialog.current?.close();
    },
  });
  const toggleEnabled = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      unwrap(await api.POST('/api/v1/users/{id}/enabled', { params: { path: { id } }, body: { enabled } })),
    onSuccess: invalidate,
  });

  const fillFromAgent = (agentId: string) => {
    const a = agents.data?.find((x) => x.id === agentId);
    if (!a) return setUserForm((f) => ({ ...f, agentId }));
    const slug = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '.').replace(/^\.|\.$/g, '');
    setUserForm((f) => ({ ...f, agentId, firstName: a.firstName, lastName: a.lastName, username: `${slug(a.firstName)}.${slug(a.lastName)}`, email: a.email ?? f.email }));
  };
  const generatePassword = () => {
    const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const bytes = crypto.getRandomValues(new Uint8Array(14));
    setUserForm((f) => ({ ...f, temporaryPassword: Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('') }));
  };

  return (
    <>
      <PageHead
        eyebrow="Administration"
        title="Comptes et rôles"
        actions={
          isAdmin && (
            <>
              <button className="btn" onClick={() => userDialog.current?.showModal()}>
                Créer un compte
              </button>
              <button className="btn btn-primary" onClick={() => roleDialog.current?.showModal()}>
                Attribuer un rôle
              </button>
            </>
          )
        }
      >
        Les comptes vivent dans le portail de connexion ; les rôles décident de ce que chacun voit et fait dans la collectivité.
      </PageHead>

      {created && (
        <div className="note">
          <strong>Compte {created.username} créé.</strong> Mot de passe provisoire à transmettre en main propre : <code>{created.password}</code>. Il devra être changé à la première connexion. Ce mot de passe n'est enregistré nulle part.{' '}
          <button className="btn btn-sm" onClick={() => setCreated(null)}>
            J'ai noté
          </button>
        </div>
      )}

      <section className="stack" style={{ marginBottom: 24 }}>
        <h2>Rôles attribués</h2>
        <ErrorNote error={roles.error ?? revoke.error} />
        {roles.isPending ? (
          <Loading />
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Compte</th>
                  <th>Rôle</th>
                  <th>Périmètre</th>
                  <th>Depuis</th>
                  <th>Jusqu'au</th>
                  <th>Délégation</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(roles.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.userLabel}</strong>
                    </td>
                    <td>
                      <RoleBadge role={r.role} />
                    </td>
                    <td className="mono">{serviceLabel(r.serviceId)}</td>
                    <td className="small">{formatDateTime(r.validFrom)}</td>
                    <td className="small">{r.validTo ? formatDateTime(r.validTo) : '—'}</td>
                    <td className="small muted">{r.delegatedBy ? `par ${userLabel(r.delegatedBy)}` : '—'}</td>
                    <td>
                      {isAdmin && (
                        <button className="btn btn-sm" disabled={revoke.isPending} onClick={() => window.confirm(`Clôturer le rôle ${r.role} de ${r.userLabel} ?`) && revoke.mutate(r.id)}>
                          Clôturer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="stack">
        <h2>Comptes de la collectivité</h2>
        <ErrorNote error={users.error ?? toggleEnabled.error} />
        {users.isPending ? (
          <Loading />
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Identifiant</th>
                  <th>Nom</th>
                  <th>Courriel</th>
                  <th>État</th>
                  <th>À la prochaine connexion</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((u) => (
                  <tr key={u.id}>
                    <td className="mono">{u.username}</td>
                    <td>
                      {u.lastName?.toUpperCase()} {u.firstName}
                    </td>
                    <td className="muted">{u.email ?? '—'}</td>
                    <td>{u.enabled ? <span className="b b-ok">Actif</span> : <span className="b b-bad">Désactivé</span>}</td>
                    <td className="small muted">
                      {u.requiredActions.map((a) => (a === 'UPDATE_PASSWORD' ? 'changer le mot de passe' : a === 'CONFIGURE_TOTP' ? 'enrôler la MFA' : a)).join(', ') || '—'}
                    </td>
                    <td>
                      {isAdmin && (
                        <button className="btn btn-sm" disabled={toggleEnabled.isPending} onClick={() => toggleEnabled.mutate({ id: u.id, enabled: !u.enabled })}>
                          {u.enabled ? 'Désactiver' : 'Réactiver'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <dialog ref={roleDialog} aria-labelledby="role-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            assign.mutate();
          }}
        >
          <h2 id="role-title">Attribuer un rôle</h2>
          <div className="field">
            <label htmlFor="r-user">Compte</label>
            <select id="r-user" required value={roleForm.userId} onChange={(e) => setRoleForm((f) => ({ ...f, userId: e.target.value }))}>
              <option value="">— Choisir —</option>
              {(users.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username} · {u.lastName?.toUpperCase()} {u.firstName}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="r-role">Rôle</label>
            <select id="r-role" value={roleForm.role} onChange={(e) => setRoleForm((f) => ({ ...f, role: e.target.value }))}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {roleForm.role === 'chef_service' && (
            <div className="field">
              <label htmlFor="r-service">Service dirigé</label>
              <select id="r-service" required value={roleForm.serviceId} onChange={(e) => setRoleForm((f) => ({ ...f, serviceId: e.target.value }))}>
                <option value="">— Choisir —</option>
                {(services.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="r-until">Fin (facultatif)</label>
            <input id="r-until" type="date" value={roleForm.validTo} onChange={(e) => setRoleForm((f) => ({ ...f, validTo: e.target.value }))} />
          </div>
          <ErrorNote error={assign.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={assign.isPending}>
              Attribuer
            </button>
            <button type="button" className="btn" onClick={() => roleDialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>

      <dialog ref={userDialog} aria-labelledby="user-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createUser.mutate();
          }}
        >
          <h2 id="user-title">Créer un compte</h2>
          <div className="field">
            <label htmlFor="u-agent">Pour l'agent (pré-remplit et relie le dossier)</label>
            <select id="u-agent" value={userForm.agentId} onChange={(e) => fillFromAgent(e.target.value)}>
              <option value="">— Sans dossier d'agent —</option>
              {(agents.data ?? [])
                .filter((a) => !a.keycloakUserId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.lastName.toUpperCase()} {a.firstName} · {a.matricule}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="u-last">Nom</label>
              <input id="u-last" required value={userForm.lastName} onChange={(e) => setUserForm((f) => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div className="field">
              <label htmlFor="u-first">Prénom</label>
              <input id="u-first" required value={userForm.firstName} onChange={(e) => setUserForm((f) => ({ ...f, firstName: e.target.value }))} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="u-username">Identifiant</label>
            <input id="u-username" required pattern="[a-z0-9._-]{3,64}" value={userForm.username} onChange={(e) => setUserForm((f) => ({ ...f, username: e.target.value }))} />
            <span className="hint">minuscules, chiffres, point, tiret</span>
          </div>
          <div className="field">
            <label htmlFor="u-email">Courriel</label>
            <input id="u-email" type="email" required value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="u-pwd">Mot de passe provisoire</label>
            <div className="actions">
              <input id="u-pwd" required minLength={12} value={userForm.temporaryPassword} onChange={(e) => setUserForm((f) => ({ ...f, temporaryPassword: e.target.value }))} style={{ flex: 1 }} autoComplete="off" />
              <button type="button" className="btn btn-sm" onClick={generatePassword}>
                Générer
              </button>
            </div>
            <span className="hint">À changer à la première connexion. Transmettez-le en main propre.</span>
          </div>
          <div className="field">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={userForm.requireTotp} onChange={(e) => setUserForm((f) => ({ ...f, requireTotp: e.target.checked }))} />
              Imposer l'application d'authentification (MFA) dès la première connexion
            </label>
            <span className="hint">Recommandé pour les chefs de service, obligatoire pour DRH, élus et administrateurs.</span>
          </div>
          <ErrorNote error={createUser.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={createUser.isPending}>
              Créer le compte
            </button>
            <button type="button" className="btn" onClick={() => userDialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
